/**
 * Pure types/converters for subsystem-driven navigation — the generated
 * command interface. Mirrors core/designer/subsystem_service.py; `object_name`
 * keeps the wire's snake_case on purpose.
 */
import type { ConfigNavItem } from './configMenuNav';

export interface SubsystemItem {
  id: string;
  label: string;
  icon?: string;
  route?: string;
  object_name?: string;
  kind?: string;
}

export interface SubsystemGroup {
  kind: string;
  label: string;
  items: SubsystemItem[];
}

export interface Subsystem {
  name: string;
  label: string;
  icon?: string;
  order: number;
  module?: string;
  groups: SubsystemGroup[];
  children: Subsystem[];
}

function itemToNav<TIcon>(
  item: SubsystemItem,
  resolveIcon: (name: string | undefined) => TIcon,
): ConfigNavItem<TIcon> {
  return { text: item.label, icon: resolveIcon(item.icon), path: item.route || '' };
}

function toNav<TIcon>(
  subsystem: Subsystem,
  resolveIcon: (name: string | undefined) => TIcon,
): ConfigNavItem<TIcon> {
  const groups = subsystem.groups;
  // One group of items needs no group row — the section IS the group.
  const groupNodes: ConfigNavItem<TIcon>[] =
    groups.length === 1
      ? groups[0].items.map((i) => itemToNav(i, resolveIcon))
      : groups.map((g) => ({
          text: g.label,
          icon: resolveIcon(undefined),
          path: '',
          id: `${subsystem.name}:${g.kind}`,
          kind: g.kind,
          children: g.items.map((i) => itemToNav(i, resolveIcon)),
        }));
  const childNodes = subsystem.children.map((c) => toNav(c, resolveIcon));
  const children = [...groupNodes, ...childNodes];
  return {
    text: subsystem.label,
    icon: resolveIcon(subsystem.icon),
    path: '',
    id: subsystem.name,
    ...(children.length > 0 ? { children } : {}),
  };
}

export function subsystemsToNavItems<TIcon>(
  subsystems: Subsystem[],
  resolveIcon: (name: string | undefined) => TIcon,
): ConfigNavItem<TIcon>[] {
  return subsystems.map((s) => toNav(s, resolveIcon));
}

export function navNodeKey(item: { id?: string; path: string; text: string }): string {
  return item.id || item.path || item.text;
}

const GROUP_KIND_I18N: Record<string, string> = {
  catalogs: 'nav.catalogs',
  documents: 'nav.documents',
  reports: 'nav.reports',
  registers: 'nav.registers',
  dataProcessors: 'nav.dataProcessors',
  custom: 'nav.other',
};

/** Chrome labels for kind groups — backend ships English; render through i18n. */
export function groupKindLabel(
  kind: string | undefined,
  fallback: string,
  t: (key: string, defaultValue: string) => string,
): string {
  if (!kind) return fallback;
  const key = GROUP_KIND_I18N[kind];
  return key ? t(key, fallback) : fallback;
}

export interface SectionRow<T> {
  item: T;
  key: string;
  group: boolean;
}

/**
 * Walk one extra depth (and nested groups) so a two-group section yields
 * kind-group headers plus their navigable items — the renderer is one-level.
 */
export function walkSectionRows<T extends { id?: string; path: string; text: string; children?: T[] }>(
  section: T,
): SectionRow<T>[] {
  const rows: SectionRow<T>[] = [];
  const walk = (nodes: T[], parentKey: string) => {
    for (const node of nodes) {
      const key = navNodeKey(node) || `${parentKey}/${node.text}`;
      if (node.children && node.children.length > 0) {
        rows.push({ item: node, key, group: true });
        walk(node.children, key);
      } else {
        rows.push({ item: node, key, group: false });
      }
    }
  };
  walk(section.children ?? [], navNodeKey(section));
  return rows;
}
